import { ColumnDef } from '@tanstack/react-table';
import { AppTableFeatures } from '~/components/table/appTableFeatures';
import { camelCase } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Table } from '~/components/table/Table';
import { formatCurrency } from '~/helpers/formatCurrency';
import { formatDate } from '~/helpers/formatDate';
import { StockMovement } from '~/types';

type Props = {
  data: StockMovement[];
  isLoading?: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  [key: string]: any;
};

export const StockMovementTable = ({
  data,
  isLoading,
  containerRef,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: stockMovementId } = useParams();

  const columns = useMemo<ColumnDef<AppTableFeatures, StockMovement>[]>(
    () => [
      { header: 'Seq', accessorKey: 'id', enableSorting: false },
      {
        header: t('Occurred At'),
        accessorKey: 'occurredAt',
        cell: (info) => formatDate(info.getValue<string | null | undefined>()),
        size: 140,
      },
      {
        header: t('Location Type'),
        accessorKey: 'locationType',
        cell: (info) => t(camelCase(info.getValue<string>() ?? '')),
        size: 100,
      },
      {
        header: t('Location'),
        accessorKey: 'locationName',
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        header: t('Space'),
        accessorKey: 'spaceName',
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        header: t('sku'),
        accessorKey: 'skuCode',
        cell: (info) => {
          const { skuName, skuCode } = info.row.original;
          return (
            <>
              <div className='font-semibold'>{skuName}</div>
              {skuCode && <div className='text-copy-alt'>{skuCode}</div>}
            </>
          );
        },
        size: 350,
      },
      {
        header: t('Type'),
        id: 'type',
        accessorFn: (row) => row.adjustment_type || row.type,
        cell: (info) => info.getValue(),
        size: 110,
      },
      {
        header: t('Quantity'),
        accessorKey: 'quantity',
        cell: (info) => info.getValue(),
        meta: { align: 'right' },
        size: 80,
      },
      {
        header: t('price'),
        accessorKey: 'price',
        cell: (info) =>
          formatCurrency(info.getValue<number | null | undefined>()),
        meta: { align: 'right' },
        size: 80,
      },
      {
        header: t('total'),
        accessorKey: 'total',
        cell: (info) =>
          formatCurrency(info.getValue<number | null | undefined>()),
        meta: { align: 'right' },
        size: 80,
      },
      {
        header: 'New SOH',
        accessorKey: 'newSoh',
        cell: (info) => info.getValue(),
        meta: { align: 'right' },
        size: 80,
      },
      {
        header: t('Notes'),
        accessorKey: 'notes',
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        header: t('Entered By'),
        accessorKey: 'enteredBy',
        cell: (info) => info.getValue(),
        size: 100,
      },
      {
        header: t('Entered At'),
        accessorKey: 'enteredAt',
        cell: (info) => formatDate(info.getValue<string | null | undefined>()),
        size: 140,
      },
    ],
    [t]
  );

  const initialHiddenColumns = useMemo(() => ['id'], []);
  const initialSortBy = useMemo(
    () => [
      { id: 'occurredAt', desc: true },
      { id: 'id', desc: true },
    ],
    []
  );

  return (
    <div className='h-full overflow-auto'>
      <Table<StockMovement>
        columns={columns}
        data={data}
        containerRef={containerRef}
        initialHiddenColumns={initialHiddenColumns}
        initialSortBy={initialSortBy}
        activeId={stockMovementId}
        isLoading={isLoading}
        onRowClick={(row: any) => {
          if (!row.original?.isSkeleton) {
            navigate(`./${row.original.id}`);
          }
        }}
      />
    </div>
  );
};
